import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { Auth0Provider, useAuth0, type AppState } from '@auth0/auth0-react';
import type { AppRole } from '@/types';
import { ROLES, highestRole, isAppRole, meetsMinimum } from '@/auth/roles';
import { getIdentityProviderInfo, type IdentityProviderInfo } from '@/auth/identityProvider';

const CLAIMS_NAMESPACE = import.meta.env.VITE_CLAIMS_NAMESPACE ?? 'https://iris-northamerica.com/';
export const ROLES_CLAIM = `${CLAIMS_NAMESPACE}roles`;
export const EMAIL_CLAIM = `${CLAIMS_NAMESPACE}email`;

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

export interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  userName?: string;
  userEmail?: string;
  userPictureUrl?: string;
  /** Auth0 `sub` of the signed-in user (matches `auth0Id` on backend user rows). */
  userSub?: string;
  /** The connection the current user authenticated with (Okta, Google, database, etc.). */
  identityProvider: IdentityProviderInfo;
  /** All recognized crm:* roles present on the id token. */
  roles: AppRole[];
  /** The single highest-ranked role, or null when the account has no role assigned. */
  role: AppRole | null;
  isAdmin: boolean;
  isEditorOrAbove: boolean;
  isAnalystOrAbove: boolean;
  hasRole: (role: AppRole) => boolean;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/** Reads role/email custom claims from the Auth0 id token and exposes helpers. */
function AuthContextBridge({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, user, loginWithRedirect, logout } = useAuth0();
  const userSub = user?.sub;

  // Push the Auth0 user ID (not the email) to GTM's dataLayer for user-level tracking.
  useEffect(() => {
    if (!isAuthenticated || !userSub) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'user_login', user_id: userSub });
  }, [isAuthenticated, userSub]);

  const value = useMemo<AuthContextValue>(() => {
    // Unrecognized claim values are ignored; a user with only unknown roles is
    // treated the same as a user with no role at all.
    const rawRoles = (user?.[ROLES_CLAIM] as unknown[] | undefined) ?? [];
    const role = highestRole(rawRoles);
    const roles = rawRoles.filter(isAppRole);
    return {
      isAuthenticated,
      isLoading,
      userName: user?.name,
      userEmail: (user?.[EMAIL_CLAIM] as string | undefined) ?? user?.email,
      userPictureUrl: user?.picture,
      userSub,
      identityProvider: getIdentityProviderInfo(user?.sub),
      roles,
      role,
      isAdmin: role === ROLES.Admin,
      isEditorOrAbove: meetsMinimum(role, ROLES.Editor),
      isAnalystOrAbove: meetsMinimum(role, ROLES.Analyst),
      hasRole: (r: AppRole) => roles.includes(r),
      login: () => void loginWithRedirect(),
      logout: () => void logout({ logoutParams: { returnTo: window.location.origin } }),
    };
  }, [isAuthenticated, isLoading, user, userSub, loginWithRedirect, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * After Auth0 redirects back to the app, return the user to the page they
 * were on when the session expired (set by AuthTokenBridge / ProtectedRoute).
 * Uses the History API + popstate so the data router picks up the change
 * without this module depending on the router instance.
 */
function onRedirectCallback(appState?: AppState) {
  const returnTo = appState?.returnTo ?? sessionStorage.getItem('postLoginRedirect') ?? undefined;
  sessionStorage.removeItem('postLoginRedirect');
  if (returnTo && returnTo.startsWith('/') && returnTo !== window.location.pathname) {
    window.history.replaceState(null, '', returnTo);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }
}

export function AppAuthProvider({ children }: { children: ReactNode }) {
  return (
    <Auth0Provider
      onRedirectCallback={onRedirectCallback}
      domain={import.meta.env.VITE_AUTH0_DOMAIN ?? ''}
      clientId={import.meta.env.VITE_AUTH0_CLIENT_ID ?? ''}
      authorizationParams={{
        redirect_uri: import.meta.env.VITE_AUTH0_REDIRECT_URI ?? window.location.origin,
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
        // No `connection` here: let Auth0's Universal Login page handle
        // identifier-first routing between the Okta enterprise connection and
        // the username/password database connection.
      }}
      useRefreshTokens
      cacheLocation="localstorage"
    >
      <AuthContextBridge>{children}</AuthContextBridge>
    </Auth0Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AppAuthProvider');
  return ctx;
}
